"use client";
import { Configure, Index } from "react-instantsearch";
import AbandonedCartSuggestions from "@/components/AbandonedCartSuggestions";
import SearchHits from "@/components/Search/components/SearchHits";
import { ReadonlyURLSearchParams, useSearchParams } from "next/navigation";
import SearchPagination from "@/components/Search/components/SearchPagination";
import SearchActiveRefinement from "@/components/Search/components/SearchActiveRefinement";
import SearchResultCount from "@/components/Search/components/SearchResultCount";
import {
  FilterVersion,
  SearchModule,
  SearchProps,
  SearchVersion,
} from "@/components/Search/types";
import {
  DEFAULT_SEARCH_CONFIG,
  RESULT_PRODUCT_TYPE_MAP,
  PRODUCT_TYPES_TO_SHOW,
  QUERY_URL_PARAM,
  PRODUCT_TO_RESULT_TYPE_MAP,
  CARDS_PER_ROW_HORIZONTAL_FILTER_PAGE,
  DEFAULT_SECTIONS_ORDER,
  CARDS_PER_PAGE,
  COURSE,
  PROGRAM,
  EXECUTIVE_EDUCATION,
  TWOU_DEGREE,
  DEFAULT_SEARCH_BASE_PATH,
} from "@/components/Search/constants";
import getResultTypeFromUrl from "@/components/Search/utils/getResultTypeFromUrl";
import {
  useIsResultTabView,
  useSearchIndex,
  useProductCreatedRange,
} from "@/components/Search/components/SearchContextProvider";
import getFilters from "@/components/Search/utils/getFilters";
import { shouldExcludeOnlyArchivedCourseRuns } from "@/components/Search/utils/getRefinementListFromSearchParams";
import { Fragment, useEffect, useMemo } from "react";
import useSearchPagePromo from "@/monarch/rules/searchPagePromo/useSearchPagePromo";
import PromoBanner from "@/components/PromoBanner";
import getProductCreatedFilters from "@/components/Search/utils/getProductCreatedFilters";
import SearchFilters from "../SearchFilters";
import useSearchFypEntry from "@/monarch/rules/searchFypEntry/useSearchFypEntry";
import type {
  SearchFypPlacement,
  SearchFypInlineSection,
} from "@/monarch/rules/searchFypEntry/types";
import {
  SEARCH_FYP_PLACEMENTS,
  SearchFypPlacementId,
} from "@/monarch/rules/searchFypEntry/types";
import SearchFypEntry from "@/components/Search/components/SearchFypEntry";
import ProductCollections from "@/components/ProductCollections";
import { useSearchMessages } from "@/i18n/hooks/useSearchMessages";
import useUrlSearchString from "@/components/Search/hooks/useUrlSearchString";
import useSyncSearchPage from "@/components/Search/hooks/useSyncSearchPage";

const DEFAULT_FYP_PLACEMENT = SearchFypPlacementId.BeforeResults;

const INLINE_SECTION_TO_PRODUCT: Record<SearchFypInlineSection, string> = {
  "after-programs": PROGRAM,
  "after-courses": COURSE,
  "after-executive-education": EXECUTIVE_EDUCATION,
  "after-degrees": TWOU_DEGREE,
};

const isSearchFypPlacement = (
  placement: string | undefined,
): placement is SearchFypPlacement => {
  return SEARCH_FYP_PLACEMENTS.includes(placement as SearchFypPlacement);
};

function SearchContent({
  searchConfig = {},
  queryString = "",
  searchBasePath = DEFAULT_SEARCH_BASE_PATH,
  resultsHeadingVariant,
}: SearchProps) {
  const authoredSearchString = useUrlSearchString(queryString);
  const routerSearchString = useSearchParams().toString();
  // /search has no authored query, so Next's search params are the source.
  // A CMS shortcode passes queryString and updates it through popstate.
  const liveSearchString = queryString.length
    ? authoredSearchString
    : routerSearchString;
  useSyncSearchPage();
  const { searchIndex } = useSearchIndex();
  const { setIsResultTabView } = useIsResultTabView();
  const { productCreatedRange } = useProductCreatedRange();
  const searchParams = useMemo(
    () =>
      new URLSearchParams(
        liveSearchString,
      ) as unknown as ReadonlyURLSearchParams,
    [liveSearchString],
  );
  const pageTab = RESULT_PRODUCT_TYPE_MAP[getResultTypeFromUrl(searchParams)];
  const query = searchParams.get(QUERY_URL_PARAM);
  const productCreatedFilters = getProductCreatedFilters(productCreatedRange);
  const configForSearch = {
    ...DEFAULT_SEARCH_CONFIG,
    filters: getFilters({
      indexName: searchIndex,
      excludeOnlyArchivedCourseRuns: shouldExcludeOnlyArchivedCourseRuns(
        searchParams,
        searchIndex,
      ),
    }),
    ...searchConfig,
    ...(productCreatedFilters.length > 0 && {
      numericFilters: productCreatedFilters,
    }),
  };
  const searchPagePromo = useSearchPagePromo();
  const data = searchPagePromo?.data;
  const pageTabPromoBannerMap = {
    [COURSE]: data?.promoBannerCourse,
    [PROGRAM]: data?.promoBannerProgram,
    [EXECUTIVE_EDUCATION]: data?.promoBannerEE,
    [TWOU_DEGREE]: data?.promoBannerDegree,
  };
  const secondLevelPromoBanner =
    pageTabPromoBannerMap[pageTab as keyof typeof pageTabPromoBannerMap] ||
    undefined;
  const fypResult = useSearchFypEntry();
  const fypData = fypResult?.data;
  const shouldRenderFyp = Boolean(fypResult?.isLoading || fypData?.isVisible);
  const fypPlacement = isSearchFypPlacement(fypData?.placement)
    ? fypData.placement
    : DEFAULT_FYP_PLACEMENT;
  const tabPagePosition = fypData?.inlineConfig?.tabPagePosition;
  const validatedTabPagePosition =
    typeof tabPagePosition === "number" && tabPagePosition >= 1
      ? Math.floor(tabPagePosition)
      : undefined;
  const inlineFypTabConfig =
    shouldRenderFyp &&
    fypPlacement === SearchFypPlacementId.InlineResults &&
    validatedTabPagePosition !== undefined
      ? {
          afterRow: validatedTabPagePosition,
          forcedFlowId: fypData?.forcedFlowId,
          jumpToStepId: fypData?.jumpToStepId,
          styleVariant: fypData?.styleVariant,
          isLoading: fypResult?.isLoading,
        }
      : undefined;
  const sectionOrder = DEFAULT_SECTIONS_ORDER;
  const { getProductTypeTitle } = useSearchMessages();

  const sections: SearchModule[] = [
    {
      key: "Lines of business module",
      renderer: () => {
        return PRODUCT_TYPES_TO_SHOW.map((productFacet, index) => {
          const isFirstSection = index === 0;
          const displayCardPromoBanner =
            isFirstSection && data?.firstLevelPromoBanner?.variant === "card";
          const displayCompactPromoBanner =
            isFirstSection &&
            data?.firstLevelPromoBanner?.variant === "compact";
          const displaySplitPromoBanner =
            isFirstSection && data?.firstLevelPromoBanner?.variant === "split";

          const hitsPerPage = displayCardPromoBanner
            ? CARDS_PER_ROW_HORIZONTAL_FILTER_PAGE - 1
            : displaySplitPromoBanner
              ? CARDS_PER_ROW_HORIZONTAL_FILTER_PAGE - 2
              : CARDS_PER_ROW_HORIZONTAL_FILTER_PAGE;

          return (
            <Fragment key={productFacet}>
              <Index indexName={searchIndex} indexId={productFacet}>
                <Configure
                  {...configForSearch}
                  hitsPerPage={hitsPerPage}
                  facetsRefinements={{ product: [productFacet] }}
                />
                <SearchHits
                  title={getProductTypeTitle(productFacet)}
                  facetForLink={PRODUCT_TO_RESULT_TYPE_MAP[productFacet]}
                  searchVersion={SearchVersion.HorizontalFilterV1}
                  searchBasePath={searchBasePath}
                  promoBannerData={
                    displayCardPromoBanner || displaySplitPromoBanner
                      ? data?.firstLevelPromoBanner
                      : undefined
                  }
                />
              </Index>
              {displayCompactPromoBanner && data?.firstLevelPromoBanner && (
                <PromoBanner
                  {...data.firstLevelPromoBanner}
                  className="my-4 order-first"
                />
              )}
              {shouldRenderFyp &&
                fypPlacement === SearchFypPlacementId.InlineResults &&
                fypData?.inlineConfig?.firstPagePosition &&
                INLINE_SECTION_TO_PRODUCT[
                  fypData.inlineConfig.firstPagePosition
                ] === productFacet && (
                  <div className="my-4">
                    <SearchFypEntry
                      placement={SearchFypPlacementId.InlineResults}
                      forcedFlowId={fypData?.forcedFlowId}
                      jumpToStepId={fypData?.jumpToStepId}
                      styleVariant={fypData?.styleVariant}
                      isLoading={fypResult?.isLoading}
                    />
                  </div>
                )}
              {productFacet === COURSE && (
                <ProductCollections
                  location="search-middle"
                  className="order-first py-4"
                />
              )}
            </Fragment>
          );
        });
      },
    },
  ];

  if (pageTab) {
    configForSearch.facetsRefinements = { product: [pageTab] };
  }
  if (query) {
    configForSearch.query = query;
  }
  useEffect(() => {
    if (pageTab) {
      setIsResultTabView(true);
    }
  }, [pageTab]);

  const renderFypAt = (placement: SearchFypPlacement) =>
    shouldRenderFyp &&
    fypPlacement === placement && (
      <div className="my-4">
        <SearchFypEntry
          placement={placement}
          forcedFlowId={fypData?.forcedFlowId}
          jumpToStepId={fypData?.jumpToStepId}
          styleVariant={fypData?.styleVariant}
          isLoading={fypResult?.isLoading}
        />
      </div>
    );

  return (
    <>
      <Configure {...configForSearch} />
      <div className="container pt-8">
        <SearchResultCount
          filterVersion={FilterVersion.HorizontalFilter}
          resultsHeadingVariant={resultsHeadingVariant}
        />
        {renderFypAt(SearchFypPlacementId.BeforeFilters)}

        <div className="flex flex-col gap-5">
          {/* Horizontal Filter */}
          <SearchFilters />

          {(pageTab || productCreatedRange) && (
            <div className="flex justify-start">
              <SearchActiveRefinement />
            </div>
          )}

          {/* Pagination */}
          {pageTab ? (
            <div className="flex justify-end">
              <SearchPagination searchParams={searchParams} />
            </div>
          ) : null}

          <AbandonedCartSuggestions />
          {renderFypAt(SearchFypPlacementId.AfterAbandonedCart)}
          {renderFypAt(SearchFypPlacementId.BeforeResults)}

          <ProductCollections location="search-above" />

          {/* Results */}
          <div className="flex flex-col" data-testid="search-results">
            {pageTab ? (
              <>
                {/* TAB RESULTS */}
                <Index indexName={searchIndex} indexId={pageTab}>
                  <Configure
                    hitsPerPage={
                      secondLevelPromoBanner?.variant === "split"
                        ? CARDS_PER_PAGE - 2
                        : secondLevelPromoBanner?.variant === "card"
                          ? CARDS_PER_PAGE - 1
                          : CARDS_PER_PAGE
                    }
                  />
                  <SearchHits
                    level="second"
                    searchVersion={SearchVersion.HorizontalFilterV1}
                    searchBasePath={searchBasePath}
                    promoBannerData={secondLevelPromoBanner}
                    inlineFypConfig={inlineFypTabConfig}
                  />
                </Index>
              </>
            ) : (
              sectionOrder.map((sectionOrderItem) =>
                sections
                  .find((section) => section.key === sectionOrderItem.section)
                  ?.renderer(),
              )
            )}
            {pageTab ? (
              <div className="flex justify-center my-4">
                <SearchPagination searchParams={searchParams} />
              </div>
            ) : null}
          </div>
          {renderFypAt(SearchFypPlacementId.AfterResults)}
          <ProductCollections location="search-below" className="pb-16" />
        </div>
      </div>
    </>
  );
}

export default SearchContent;
